import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../Card";
import {
  addStop,
  getStop,
  updateStop,
} from "../../services/OrganisationServices/transportManagemetService";
import {
  callToast,
  showErrorMessages,
} from "../../services/utilityFunctions.js";
import { useGlobalState } from "../../services/hostelConst";

const AddStopForm = ({ refresh, navigate }) => {
  const { closeModal, masterTableState, updateMasterTable, modalState } =
    useGlobalState();
  const id = modalState.id;

  const [loadingData, setLoadingData] = useState(modalState.isEditing);

  const prepareFormData = (data = null) => {
    return {
      name: data == null ? "" : data.NAME,
      description: data == null ? "" : data.DESCRIPTION,
      latitude: data == null ? "" : data.LATITUDE,
      longitude: data == null ? "" : data.LONGITUDE,
      address: data == null ? "" : data.ADDRESS,
      landmark: data == null ? "" : data.LANDMARK,
    };
  };

  const [formData, setFormData] = useState(prepareFormData());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const getStopDetailsData = async (id) => {
    setLoadingData(true);
    var res = await getStop(id);
    if (res.status == "success") {
      setFormData(prepareFormData(res.data));
    } else {
      showErrorMessages({ data: res, navigate });
      closeModal();
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (modalState.isEditing && id) {
      getStopDetailsData(id);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Stop name is required";
    }
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        address: formData.address,
        landmark: formData.landmark,
      };

      let res;
      if (modalState.isEditing) {
        submitData.entityId = id;
        res = await updateStop(submitData);
      } else {
        res = await addStop(submitData);
      }

      if (res.status === "success") {
        callToast(
          "success",
          "Success",
          `Stop ${modalState.isEditing ? "updated" : "added"} successfully`
        );
        updateMasterTable(true);
        closeModal();
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error submitting stop:", error);
    }
    setLoading(false);
  };

  if (loadingData) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Stop Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter stop name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Address *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  isInvalid={!!errors.address}
                  placeholder="Enter complete address"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Latitude</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => handleChange("latitude", e.target.value)}
                  placeholder="Enter latitude"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Longitude</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleChange("longitude", e.target.value)}
                  placeholder="Enter longitude"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Landmark</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.landmark}
                  onChange={(e) => handleChange("landmark", e.target.value)}
                  placeholder="Enter nearby landmark"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Enter stop description"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {modalState.isEditing ? "Updating..." : "Adding..."}
                </>
              ) : modalState.isEditing ? (
                "Update Stop"
              ) : (
                "Add Stop"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddStopForm;
